import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { useApiQuery, invalidateQueries } from '@/hooks/useApiQuery';
import usersService, { UserEstablishment } from '@/services/api/users.service';
import { useAuth } from '@/hooks/useAuth';
import { clearAllPermissionCache } from '@/utils/permissionCache';
import { logger } from '@/utils/logger';
import { Role, RoleLabel } from '@/constants/roles';
import { useQuizStore } from '@/stores/useQuizStore';

const POST_RELOAD_NAVIGATE_KEY = 'mep_post_reload_navigate_to';

interface EstablishmentContextType {
  currentEstablishment: UserEstablishment | null;
  establishments: UserEstablishment[];
  setCurrentEstablishment: (establishment: UserEstablishment) => void;
  /**
   * Troca o establishment corrente pelo id e, opcionalmente, navega para
   * uma rota após o reload de página disparado por `setCurrentEstablishment`.
   * Centraliza a troca para garantir reset consistente de caches e quiz store.
   */
  switchEstablishment: (id: string, opts?: { navigateTo?: string }) => void;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  getFormattedRoles: (roles?: string[]) => string;
  isSwitching: boolean;
}

const EstablishmentContext = createContext<EstablishmentContextType | undefined>(undefined);

const ESTABLISHMENT_STORAGE_KEY = 'mep_current_establishment';

export const EstablishmentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, establishments: authEstablishments, establishmentsLoading, isLoggingOut: authIsLoggingOut } = useAuth();
  const [currentEstablishment, setCurrentEstablishmentState] = useState<UserEstablishment | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  // Check if we should fetch establishments
  const hasValidToken = !!localStorage.getItem('mep_token');
  const shouldFetchEstablishments = isAuthenticated && hasValidToken && !isLoggingOut && !authIsLoggingOut;

  // Fetch full establishments with permissions from API
  const { data: apiEstablishments, error, refetch } = useApiQuery(
    'establishments:user',
    () => usersService.getUserEstablishments(),
    {
      enabled: shouldFetchEstablishments,
      cacheTime: 10 * 60 * 1000,
    }
  );

  // Use API data if available, otherwise use basic data from AuthContext
  // This allows immediate rendering with basic data while full permissions load
  const establishments: UserEstablishment[] = useMemo(() => {
    // Prioritize API data if available and complete
    if (apiEstablishments && apiEstablishments.length > 0) {
      logger.debug('[EstablishmentContext] Using API establishments', {
        module: 'establishment-context',
        action: 'resolve-establishments',
        metadata: {
          source: 'api',
          count: apiEstablishments.length,
          ids: apiEstablishments.map(e => e.id),
        }
      });
      return apiEstablishments;
    }
    // Fallback to auth establishments (without permissions) for immediate rendering
    // Ensure we have all of them from AuthContext
    if (authEstablishments && authEstablishments.length > 0) {
      logger.debug('[EstablishmentContext] Using auth establishments as fallback', {
        module: 'establishment-context',
        action: 'resolve-establishments',
        metadata: {
          source: 'auth-fallback',
          count: authEstablishments.length,
          ids: authEstablishments.map(e => e.id),
        }
      });
      return authEstablishments.map(est => ({
        ...est,
        permissions: [],
      }));
    }
    logger.debug('[EstablishmentContext] No establishments available', {
      module: 'establishment-context',
      action: 'resolve-establishments',
      metadata: {
        apiEstablishmentsCount: apiEstablishments?.length ?? 0,
        authEstablishmentsCount: authEstablishments?.length ?? 0,
      }
    });
    return [];
  }, [apiEstablishments, authEstablishments]);

  // Only show loading if we have no establishments at all (neither from API nor from AuthContext)
  const isLoading = establishmentsLoading && (!authEstablishments || authEstablishments.length === 0);

  // Listen for auth-logout event to clear establishment state
  useEffect(() => {
    const handleLogout = () => {
      console.log('[EstablishmentContext] Received auth-logout event, clearing state');
      setCurrentEstablishmentState(null);
      setIsLoggingOut(false);
    };

    window.addEventListener('auth-logout', handleLogout);
    return () => window.removeEventListener('auth-logout', handleLogout);
  }, []);

  // Monitor token changes to detect logout
  useEffect(() => {
    const hasToken = !!localStorage.getItem('mep_token');

    if (!hasToken) {
      setIsLoggingOut(false); // Reset logging out state when token is cleared
    }
  }, []);

  // Load saved establishment from localStorage on mount
  useEffect(() => {
    // Check for token as well to avoid clearing during login process
    const hasToken = !!localStorage.getItem('mep_token');
    
    if (!isAuthenticated && !hasToken) {
      logger.debug('[EstablishmentContext] User not authenticated, clearing establishment', {
        module: 'establishment-context',
        action: 'clear-establishment'
      });
      setCurrentEstablishmentState(null);
      localStorage.removeItem(ESTABLISHMENT_STORAGE_KEY);
      setIsLoggingOut(false); // Reset logging out state
      return;
    }

    const savedEstablishmentId = localStorage.getItem(ESTABLISHMENT_STORAGE_KEY);
    logger.debug('[EstablishmentContext] Loading establishments', {
      module: 'establishment-context',
      action: 'load-establishments',
      metadata: {
        savedId: savedEstablishmentId,
        establishmentsCount: establishments.length,
        currentEstablishment: currentEstablishment?.id,
      }
    });
    if (savedEstablishmentId && establishments.length > 0) {
      // Only update state if current establishment doesn't match saved ID
      // This prevents overwriting user's selection after invalidateQueries triggers refetch
      if (currentEstablishment?.id !== savedEstablishmentId) {
        const savedEstablishment = establishments.find(e => e.id === savedEstablishmentId);
        if (savedEstablishment) {
          setCurrentEstablishmentState(savedEstablishment);
          // Ensure axios header is set
          if (window.apiClient) {
            window.apiClient.defaults.headers.common['x-establishment-id'] = savedEstablishment.id;
          }
          // Establishment is ready; refresh any auth-coupled queries (navigation)
          invalidateQueries('auth:');
        } else {
          // Saved establishment NOT found in user's establishments list
          // This happens when a different user logs in after logout
          // Clear and use first available establishment
          logger.debug('[EstablishmentContext] Saved establishment not found in user list, using first available', {
            module: 'establishment-context',
            action: 'fallback-to-first',
            metadata: {
              savedId: savedEstablishmentId,
              availableIds: establishments.map(e => e.id),
            }
          });
          const firstEstablishment = establishments[0];
          if (firstEstablishment) {
            setCurrentEstablishmentState(firstEstablishment);
            localStorage.setItem(ESTABLISHMENT_STORAGE_KEY, firstEstablishment.id);
            if (window.apiClient) {
              window.apiClient.defaults.headers.common['x-establishment-id'] = firstEstablishment.id;
            }
            invalidateQueries('auth:');
          }
        }
      }
    } else if (establishments.length > 0 && !currentEstablishment && !savedEstablishmentId) {
      // No saved establishment, select the first one
      const firstEstablishment = establishments[0];
      if (firstEstablishment) {
        setCurrentEstablishmentState(firstEstablishment);
        localStorage.setItem(ESTABLISHMENT_STORAGE_KEY, firstEstablishment.id);
        // Ensure axios header is set
        if (window.apiClient) {
          window.apiClient.defaults.headers.common['x-establishment-id'] = firstEstablishment.id;
        }
        // Establishment is ready; refresh any auth-coupled queries (navigation)
        invalidateQueries('auth:');
      }
    }
  }, [establishments, isAuthenticated, currentEstablishment]);

  const setCurrentEstablishment = (establishment: UserEstablishment) => {
    // Don't switch if it's the same establishment
    if (currentEstablishment?.id === establishment.id) {
      return;
    }

    // Show switching overlay
    setIsSwitching(true);

    // CRITICAL: Synchronously update BOTH localStorage AND axios header BEFORE React state
    // This ensures any pending or immediate API requests use the correct establishment ID
    localStorage.setItem(ESTABLISHMENT_STORAGE_KEY, establishment.id);

    // Update axios default header immediately
    if (window.apiClient) {
      window.apiClient.defaults.headers.common['x-establishment-id'] = establishment.id;
    }

    logger.debug('[EstablishmentContext] Switching establishment', {
      module: 'establishment-context',
      action: 'switch-establishment',
      metadata: {
        from: currentEstablishment?.id,
        to: establishment.id,
        establishmentName: establishment.name,
      }
    });

    // Then update React state
    setCurrentEstablishmentState(establishment);

    // Clear permission cache when switching establishments
    clearAllPermissionCache();

    // Clear wizard state (zustand persist) — sem isso, dados do condomínio
    // anterior continuam no localStorage e contaminam o próximo wizard.
    try {
      useQuizStore.persist?.clearStorage?.();
    } catch (e) {
      logger.warn('[EstablishmentContext] Falha ao limpar quiz store no switch', {
        module: 'establishment-context',
        metadata: { error: e instanceof Error ? e.message : String(e) },
      });
    }

    // Reload the page to reset all state and data
    // Using a small delay to ensure the overlay is visible
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  /**
   * Troca o establishment por id (ao invés de UserEstablishment) e
   * opcionalmente direciona pra uma rota após o reload.
   *
   * O reload é disparado por `setCurrentEstablishment`; usamos sessionStorage
   * para sobreviver ao reload e navegar até a rota desejada no próximo mount.
   */
  const switchEstablishment = (
    id: string,
    opts?: { navigateTo?: string },
  ) => {
    const target = establishments.find((e) => e.id === id);
    if (!target) {
      logger.warn('[EstablishmentContext] switchEstablishment: id desconhecido', {
        module: 'establishment-context',
        metadata: { id, availableIds: establishments.map((e) => e.id) },
      });
      return;
    }
    if (opts?.navigateTo) {
      try {
        sessionStorage.setItem(POST_RELOAD_NAVIGATE_KEY, opts.navigateTo);
      } catch {
        // sessionStorage indisponível: a navegação pós-reload simplesmente não acontece
      }
    }
    setCurrentEstablishment(target);
  };

  // Após reload, se houver rota guardada, navegamos uma única vez.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const target = sessionStorage.getItem(POST_RELOAD_NAVIGATE_KEY);
      if (target) {
        sessionStorage.removeItem(POST_RELOAD_NAVIGATE_KEY);
        // Navegação via history API; o React Router consome a mudança.
        window.history.pushState({}, '', target);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    } catch {
      // ignore
    }
  }, []);

  const getFormattedRoles = (roles?: string[]): string => {
    const rolesToFormat = roles || currentEstablishment?.roles || [];
    
    if (rolesToFormat.length === 0) {
      return 'Síndico';
    }

    return rolesToFormat
      .map(role => {
        const entry = Object.entries(Role).find(([, value]) => value === role);
        if (!entry) return role;
        return RoleLabel[entry[1] as keyof typeof RoleLabel];
      })
      .join(', ');
  };

  const value: EstablishmentContextType = {
    currentEstablishment,
    establishments,
    setCurrentEstablishment,
    switchEstablishment,
    loading: isLoading,
    error: error as Error | null,
    refetch,
    getFormattedRoles,
    isSwitching,
  };

  return (
    <EstablishmentContext.Provider value={value}>
      {isSwitching && (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-white">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
            <p className="text-lg font-medium text-gray-700">
              Estamos trocando seu condomínio
            </p>
            <p className="text-sm text-gray-500">Aguarde...</p>
          </div>
        </div>
      )}
      {children}
    </EstablishmentContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useEstablishment = () => {
  const context = useContext(EstablishmentContext);
  if (context === undefined) {
    throw new Error('useEstablishment must be used within an EstablishmentProvider');
  }
  return context;
};
